import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { motion } from "framer-motion";
import { Bell, LayoutList, Search, Send, ShieldCheck, Sparkles, Users, Wand2 } from "lucide-react";
import { useState } from "react";

type Feature = {
  icon: React.ReactNode;
  title: string;
  description: string;
  impact: string;
  premium?: boolean;
};

export const FeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState<number | null>(null);

  const features: Feature[] = [
    {
      icon: <Search />,
      title: t`Multi-source sourcing`,
      description: t`Thousands of jobs from Adzuna, Remotive and the official ATS boards (Greenhouse, Lever, Ashby) of top companies.`,
      impact: t`Clean, structured listings, no fragile scraping.`,
      premium: false,
    },
    {
      icon: <Sparkles />,
      title: t`"For You" ranked feed`,
      description: t`Every job scored for fit against your resume and sorted best-match first.`,
      impact: t`Stop scrolling. See your best matches first.`,
      premium: false,
    },
    {
      icon: <Users />,
      title: t`Expert AI panel`,
      description: t`A strict ATS engine, a recruiter, a resume analyst and an interview coach review your fit for each job.`,
      impact: t`Know exactly why you'd be shortlisted, or not.`,
      premium: true,
    },
    {
      icon: <Wand2 />,
      title: t`Tailored resume & cover letter`,
      description: t`A rewritten resume and a bespoke cover letter for every job, never inventing anything.`,
      impact: t`Apply with materials built for the role.`,
      premium: true,
    },
    {
      icon: <Send />,
      title: t`One-click apply`,
      description: t`The real application form, pre-filled by AI and ready to submit in one click.`,
      impact: t`90% of the work, done for you.`,
      premium: true,
    },
    {
      icon: <ShieldCheck />,
      title: t`Scam & ghost-job radar`,
      description: t`Flags fraudulent and ghost postings before you waste your time.`,
      impact: t`Apply with confidence.`,
      premium: true,
    },
    {
      icon: <LayoutList />,
      title: t`Application tracker`,
      description: t`A Kanban board of every application, from "to apply" to "offer".`,
      impact: t`Your whole search, at a glance.`,
      premium: false,
    },
    {
      icon: <Bell />,
      title: t`Autonomous 24/7 search`,
      description: t`The platform re-scrapes your saved searches around the clock and alerts you to the best new jobs.`,
      impact: t`Set it once. It works while you sleep.`,
      premium: true,
    },
  ];

  return (
    <section
      id="features"
      className="relative bg-gradient-to-b from-transparent to-neutral-50/50 py-12 dark:to-neutral-900/30"
    >
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: "easeOut",
            },
          }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <h2 className="mb-6 text-4xl font-bold text-neutral-800 dark:text-neutral-200 md:text-5xl">
            {t`Everything to land the job, in one place.`}
          </h2>
          <p className="mx-auto max-w-4xl text-lg text-neutral-600 dark:text-neutral-400">
            {t`Not just a resume builder: a full AI job-search platform that finds the right jobs, tailors your application, and follows every step from search to interview.`}
          </p>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{
                opacity: 1,
                scale: 1,
                transition: {
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: "easeOut",
                },
              }}
              viewport={{ once: true }}
              className={cn(
                "group relative rounded-2xl border p-6 transition-all duration-300",
                feature.premium
                  ? "border-primary/20 bg-primary/5 hover:border-primary/40"
                  : "border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900/50",
                activeFeature === index ? "scale-105 shadow-lg" : "hover:shadow-xl",
              )}
              onHoverStart={() => {
                setActiveFeature(index);
              }}
              onHoverEnd={() => {
                setActiveFeature(null);
              }}
            >
              <div className="mb-4 flex items-center">
                <div
                  className={cn(
                    "mr-4 rounded-xl p-3 transition-all duration-300",
                    feature.premium
                      ? "bg-primary/10 text-primary"
                      : "bg-neutral-200/50 text-neutral-600 dark:bg-neutral-800/50 dark:text-neutral-400",
                  )}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-neutral-800 dark:text-neutral-200">
                  {feature.title}
                  {feature.premium && (
                    <span className="ml-2 rounded-full bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
                      {t`Pro`}
                    </span>
                  )}
                </h3>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {feature.description}
                </p>
                <p className="text-xs font-medium italic text-primary/80">{feature.impact}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              delay: 0.5,
              ease: "easeOut",
            },
          }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="italic text-neutral-600 dark:text-neutral-400">
            {t`Our commitment: transforming every application into a career opportunity.`}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
